// @/components/elements/diagram-component-card.tsx

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DiagramComponent } from "@/types/diagram";
import { schemaLoader } from "@/lib/component-schema.lib";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useDiagram } from "@/context/diagram-context";
import { useLibrary } from "@/context/library-context";
import { componentLibraryManager } from "@/lib/component.lib";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import MetadataForm from "@/components/ui/metadata-form";

// Utility function for handling events with stopPropagation
const handleWithStopPropagation = (
  event: React.MouseEvent,
  handler: () => void
) => {
  event.stopPropagation();
  handler();
};

interface DiagramComponentCardProps {
  component: DiagramComponent;
  index: number;
  parentIndex: number | null;
  isFirst: boolean;
  onScrollToParent: (parentId: string) => void;
}

const DiagramComponentCard: React.FC<DiagramComponentCardProps> = ({
  component,
  index,
  parentIndex,
  isFirst,
  onScrollToParent,
}) => {
  const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false);
  const diagram = useDiagram();
  const library = useLibrary();
  const availableTypes = schemaLoader.getAvailableTypes();

  // Derived state from context
  const isSelected = component.id === diagram.selectedShape;
  const isCut = component.cut && !diagram.isCopied;
  const isCopied = component.cut && diagram.isCopied;

  // Get SVG content for the component
  const getSVGContent = (): string => {
    if (component.source === "component") {
      const componentData = componentLibraryManager.getComponent(
        component.shape
      );
      return componentData?.svgContent || "";
    } else {
      const shape =
        shapesLibraryManager.getShape(component.shape) ||
        library.svgLibrary.find((s) => s.name === component.shape);
      return shape?.svgContent || "";
    }
  };

  const handleTypeChange = (type: string) => {
    diagram.updateMetadata(
      component.id,
      type === "none" ? undefined : type,
      {}
    );
  };

  const handleMetadataChange = (values: { [key: string]: any }) => {
    diagram.updateMetadata(
      component.id,
      component.type,
      values
    );
  };

  return (
    <div
      className={cn(
        "p-4 rounded-lg border cursor-pointer transition-colors",
        !isSelected
          ? "bg-surface-elevated hover:bg-surface-hover border-primary text-white"
          : "bg-surface-active  border-surface-border text-white"
      )}
      onClick={(e) =>
        handleWithStopPropagation(e, () => diagram.selectShape(component.id))
      }
    >
      <div className="flex gap-2 items-center mb-2">
        <div className="w-12 h-12 bg-white rounded overflow-hidden">
          <img
            src={`data:image/svg+xml;base64,${btoa(getSVGContent())}`}
            alt={component.shape}
            className="w-full h-full object-contain"
            onError={(e) => {
              // Fallback to a placeholder if SVG fails to load
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <h3 className="text-lg font-semibold">
          {component.shape} (3D-{index + 1})
        </h3>
      </div>
      <div className="flex justify-end mb-2">
        {isCut ? (
          isFirst && (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
                onClick={(e) =>
                  handleWithStopPropagation(e, () =>
                    diagram.cancelCut3DShape(component.id)
                  )
                }
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
                onClick={(e) =>
                  handleWithStopPropagation(e, () =>
                    diagram.paste3DShape(component.id)
                  )
                }
              >
                Paste
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="text-[#FFFFFF]"
                onClick={(e) =>
                  handleWithStopPropagation(e, () =>
                    diagram.remove3DShape(component.id)
                  )
                }
              >
                Remove
              </Button>
            </div>
          )
        ) : isCopied ? (
          isFirst && (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
                onClick={(e) =>
                  handleWithStopPropagation(e, () =>
                    diagram.paste3DShape(component.id)
                  )
                }
              >
                Paste
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
                onClick={(e) =>
                  handleWithStopPropagation(e, () =>
                    diagram.cancelCut3DShape(component.id)
                  )
                }
              >
                Cancel
              </Button>
            </div>
          )
        ) : (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
              onClick={(e) =>
                handleWithStopPropagation(e, () =>
                  diagram.copy3DShape(component.id)
                )
              }
            >
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF] disabled:opacity-50 disabled:!text-[#FFFFFF]"
              onClick={(e) =>
                handleWithStopPropagation(e, () =>
                  diagram.cut3DShape(component.id)
                )
              }
              disabled={isFirst}
            >
              Cut
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="text-[#FFFFFF]"
              onClick={(e) =>
                handleWithStopPropagation(e, () =>
                  diagram.remove3DShape(component.id)
                )
              }
            >
              Remove
            </Button>
          </div>
        )}
      </div>
      {!isCut && !isCopied && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
            <div>
              <span className="font-semibold text-muted">Position:</span>{" "}
              <span className="text-white">{component.position}</span>
            </div>
            <div>
              <span className="font-semibold text-muted">Relative To:</span>
              {parentIndex !== null ? (
                <button
                  className="text-primary hover:underline ml-2 transition-colors"
                  onClick={(e) =>
                    handleWithStopPropagation(e, () =>
                      onScrollToParent(component.relativeToId!)
                    )
                  }
                >
                  3D-{parentIndex + 1}
                </button>
              ) : (
                <span className="text-muted ml-2">None</span>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-1 text-white">
              Attached 2D Shapes:
            </h4>
            {component.attached2DShapes.length > 0 ? (
              <ul className="space-y-2">
                {component.attached2DShapes.map((shape, i) => (
                  <li
                    key={i}
                    className="flex justify-between items-center bg-surface-dark p-2 rounded border border-surface-border"
                  >
                    <span className="text-sm text-white">
                      {typeof shape.name === "string" ? shape.name : "shape"}{" "}
                      <span className="text-muted">
                        (attached to {shape.attachedTo})
                      </span>
                    </span>

                    <Button
                      variant="destructive"
                      size="sm"
                      className="ml-2 text-white"
                      onClick={(e) =>
                        handleWithStopPropagation(e, () =>
                          diagram.remove2DShape(component.id, i)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-muted text-sm">No 2D shapes attached</div>
            )}
          </div>

          <div className="mt-4 border-t border-surface-border pt-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-2">
                <label className="block text-sm font-medium text-muted mb-1">
                  Component Type
                </label>
                <Select
                  value={component.type || "none"}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger className="bg-surface-input border-surface-border text-white">
                    <SelectValue placeholder="Select component type" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-surface-border">
                    <SelectItem
                      value="none"
                      className="text-white hover:bg-surface-hover"
                    >
                      No Type
                    </SelectItem>
                    {availableTypes.map((type) => (
                      <SelectItem
                        key={type}
                        value={type}
                        className="text-white hover:bg-surface-hover"
                      >
                        {schemaLoader.getComponentType(type)?.displayName ||
                          type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {component.type && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-6 !text-white !bg-primary border-primary hover:!bg-primary-dark hover:!text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMetadataDialogOpen(true);
                  }}
                >
                  Edit Metadata
                </Button>
              )}
            </div>

            {component.type && component.metadata && (
              <div className="mt-2 p-2 bg-surface-dark rounded border border-surface-border">
                <h4 className="text-sm font-medium text-muted mb-1">
                  Current Metadata:
                </h4>
                <div className="text-sm text-white space-y-1">
                  {Object.entries(component.metadata).map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="font-medium text-muted min-w-0 flex-shrink-0">
                        {key}:
                      </span>{" "}
                      <span className="ml-2 break-all">
                        {value?.toString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <Dialog
        open={isMetadataDialogOpen}
        onOpenChange={setIsMetadataDialogOpen}
      >
        <DialogContent className="bg-surface border-surface-border text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Metadata -{" "}
              {schemaLoader.getComponentType(component.type || "")?.displayName}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Edit the metadata for this diagram component.
            </DialogDescription>
          </DialogHeader>
          {component.type && (
            <MetadataForm
              fields={
                schemaLoader.getComponentType(component.type)?.fields || []
              }
              values={component.metadata || {}}
              onChange={handleMetadataChange}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiagramComponentCard;
